import type { User as FirebaseUser } from 'firebase/auth';

/**
 * Keep a live session's ID token in step with its Auth custom claims.
 *
 * firestore.rules reads identity from `request.auth.token` first, so a role or
 * school change only takes effect once the client's token carries the new claims.
 * `syncUserClaimsOnWrite` writes `claims_updated_at` back onto users/{uid} for
 * exactly this purpose — and until now nothing on the client read it, so a repaired
 * or changed claim did not reach an open session until Firebase rotated the token on
 * its own, about an hour later.
 *
 * Deliberately not part of UserProfile: `sameProfile` compares the union of both
 * objects' keys, so threading claims_updated_at through the profile would give every
 * claims write a new profile identity and re-render every dashboard — the exact
 * regression 461dfc36 fixed. The listener reads it off the snapshot and hands it
 * here instead.
 */

/** Last document value we already acted on, per uid. */
const actedOn = new Map<string, number>();
/** In-flight refresh per uid, so overlapping snapshots share one call. */
const inFlight = new Map<string, Promise<void>>();
/** Refresh timestamps per uid, for the runaway backstop. */
const attempts = new Map<string, number[]>();

const MAX_REFRESHES = 3;
const WINDOW_MS = 5 * 60 * 1000;

function withinBudget(uid: string): boolean {
  const now = Date.now();
  const recent = (attempts.get(uid) ?? []).filter((t) => now - t < WINDOW_MS);
  attempts.set(uid, recent);
  return recent.length < MAX_REFRESHES;
}

function record(uid: string): void {
  attempts.set(uid, [...(attempts.get(uid) ?? []), Date.now()]);
}

/**
 * Refresh the ID token if the user document says the claims are newer than it.
 *
 * Returns true when a refresh actually happened.
 *
 * This terminates by construction: after getIdToken(true) the token carries the same
 * claims_updated_at the document has, so the next snapshot compares equal. The forced
 * refresh performs no Firestore write, so it cannot produce a snapshot of its own —
 * the only write in the cycle is the trigger's, and that one does not re-trigger
 * itself because claimFieldsChanged() sees no mirrored field change.
 *
 * The attempt budget guards the one genuine pathology: a server that bumps
 * claims_updated_at on the document but fails to put it in the token, which would
 * otherwise spin forever.
 */
export async function ensureFreshClaims(
  user: FirebaseUser,
  docClaimsUpdatedAt: unknown
): Promise<boolean> {
  const docAt = Number(docClaimsUpdatedAt ?? 0);
  if (!Number.isFinite(docAt) || docAt <= 0) return false;

  const uid = user.uid;
  if ((actedOn.get(uid) ?? 0) >= docAt) return false;

  const existing = inFlight.get(uid);
  if (existing) {
    await existing;
    return false;
  }

  const run = (async () => {
    try {
      // Cached read — no network unless the token has already expired.
      const { claims } = await user.getIdTokenResult();
      const tokenAt = Number(claims.claims_updated_at ?? 0);
      if (tokenAt >= docAt) {
        actedOn.set(uid, docAt);
        return;
      }
      if (!withinBudget(uid)) {
        console.warn(
          `[claimsRefresh] Token for ${uid} is still behind after ${MAX_REFRESHES} refreshes ` +
            `(token=${tokenAt}, document=${docAt}). Giving up until the next sign-in.`
        );
        actedOn.set(uid, docAt);
        return;
      }
      record(uid);
      await user.getIdToken(true);
      actedOn.set(uid, docAt);
    } catch (error) {
      // Leave actedOn unset so a later snapshot retries, still under the budget.
      console.warn('[claimsRefresh] Could not refresh ID token:', error);
    }
  })();

  inFlight.set(uid, run);
  try {
    await run;
  } finally {
    inFlight.delete(uid);
  }
  return true;
}

/** Forget a user's refresh bookkeeping. Call on sign-out / account switch. */
export function resetClaimsRefresh(uid?: string): void {
  if (!uid) {
    actedOn.clear();
    inFlight.clear();
    attempts.clear();
    return;
  }
  actedOn.delete(uid);
  inFlight.delete(uid);
  attempts.delete(uid);
}

/**
 * True when the token's identity disagrees with the user document.
 *
 * Used to decide whether POSTing /user/sync-claims is worth it, instead of firing it
 * blindly on every first snapshot.
 */
export async function claimsLookStale(
  user: FirebaseUser,
  documentRole: string | undefined
): Promise<boolean> {
  try {
    const { claims } = await user.getIdTokenResult();
    if (claims.claims_updated_at === undefined) return true;
    if (!documentRole) return false;
    return claims.role !== documentRole;
  } catch {
    return false;
  }
}
